import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { AuthImage } from "./AuthImage";

/**
 * Full-screen photo viewer (plain overlay — always above everything).
 * - Single photo: <PhotoLightbox path={p} open onClose />
 * - Gallery: <PhotoLightbox paths={[...]} startIndex={2} open onClose />
 *   Arrows, ←/→ keys and touch swipe move between photos.
 */
export function PhotoLightbox({ path, paths, startIndex = 0, open, onClose }) {
  const list = paths && paths.length ? paths : (path ? [path] : []);
  const isOpen = open !== undefined ? !!open : list.length > 0;
  const [idx, setIdx] = useState(startIndex);
  const [touchX, setTouchX] = useState(null);

  useEffect(() => {
    if (isOpen) setIdx(Math.min(Math.max(startIndex, 0), Math.max(list.length - 1, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, startIndex]);

  const prev = () => setIdx((i) => (i - 1 + list.length) % list.length);
  const next = () => setIdx((i) => (i + 1) % list.length);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (list.length < 2) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, list.length]);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen]);

  if (!isOpen) return null;
  const current = list[idx];

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      onTouchStart={(e) => setTouchX(e.touches[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        if (touchX == null || list.length < 2) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX;
        if (dx > 45) prev();
        else if (dx < -45) next();
        setTouchX(null);
      }}
      data-testid="photo-lightbox"
      role="dialog"
      aria-label="Photo preview"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-[max(0.75rem,env(safe-area-inset-top))] right-3 w-10 h-10 flex items-center justify-center bg-black/60 rounded-full text-white hover:bg-[#FF5F15] transition-colors z-10"
        data-testid="lightbox-close"
        aria-label="Close photo"
      >
        <X className="w-5 h-5" />
      </button>
      {current && (
        <AuthImage
          path={current}
          alt="Preview"
          className="max-w-full max-h-[100dvh] object-contain select-none"
        />
      )}
      {list.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 rounded-full text-white hover:bg-[#FF5F15] transition-colors"
            data-testid="lightbox-prev"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 rounded-full text-white hover:bg-[#FF5F15] transition-colors"
            data-testid="lightbox-next"
            aria-label="Next photo"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 bg-black/70 text-white text-[11px] font-mono px-2.5 py-1 rounded-full" data-testid="lightbox-counter">
            {idx + 1} / {list.length}
          </div>
        </>
      )}
    </div>
  );
}
